import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Info, Pause, Play, RotateCcw, TimerReset } from 'lucide-react';
import {
  TUG_REFERENCE_NOTE,
  TUG_STANDARD_PROTOCOL,
  TUG_WALKING_AID_OPTIONS,
  buildTugPayload,
  calculateTugReferenceComparison,
  formatTugSeconds,
  getTUGReferenceComparisonNote,
  getTUGInterpretationText,
  getTUGSeverityLabel,
  getTUGSeverityRangeLabel,
  getTUGSeverityTone,
  getTUGWalkingAidLabel,
  isTUGComparableToReference,
  isTugAwaitingSecondAttempt,
  normalizeTugPayload,
  normalizeTugTime,
  type TugAttemptPayload,
  type TugResultPayload,
  type TugWalkingAid,
} from '../tug/tugDomain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

type TUGPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface TUGTestProps {
  initial?: TugResultPayload | null;
  patientContext?: TUGPatientContext;
  onCancel?: () => void;
  onDraftAttempt?: (payload: TugResultPayload) => Promise<boolean | void> | boolean | void;
  onNext: (result: TugResultPayload) => void;
}

type TugAttemptFormState = {
  completed: boolean;
  timeSeconds: string;
  walkingAid: TugWalkingAid;
  customAidDescription: string;
  familiarizationPerformed: boolean;
  physicalAssistance: boolean;
  reasonNotCompleted: string;
  observations: string;
};

export type { TugResultPayload };

const formatStopwatch = (elapsedMs: number): string => {
  const totalTenths = Math.floor(elapsedMs / 100);
  const minutes = Math.floor(totalTenths / 600);
  const seconds = Math.floor((totalTenths % 600) / 10);
  const tenths = totalTenths % 10;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
};

const formatElapsedSecondsInput = (elapsedMs: number): string =>
  Number((elapsedMs / 1000).toFixed(1))
    .toString()
    .replace('.', ',');

const buildAttemptFormState = (attempt?: TugAttemptPayload | null): TugAttemptFormState => ({
  completed: attempt?.completed ?? true,
  timeSeconds: typeof attempt?.timeSeconds === 'number' ? String(attempt.timeSeconds).replace('.', ',') : '',
  walkingAid: attempt?.walkingAid ?? 'none',
  customAidDescription: attempt?.customAidDescription ?? '',
  familiarizationPerformed: attempt?.familiarizationPerformed ?? true,
  physicalAssistance: attempt?.physicalAssistance ?? false,
  reasonNotCompleted: attempt?.reasonNotCompleted ?? '',
  observations: attempt?.observations ?? '',
});

const buildFreshAttemptFormState = (attempt?: TugAttemptPayload | null): TugAttemptFormState => ({
  completed: true,
  timeSeconds: '',
  walkingAid: attempt?.walkingAid ?? 'none',
  customAidDescription: attempt?.customAidDescription ?? '',
  familiarizationPerformed: attempt?.familiarizationPerformed ?? true,
  physicalAssistance: false,
  reasonNotCompleted: '',
  observations: '',
});

const resolveElapsedMsFromForm = (form: TugAttemptFormState): number => {
  const timeSeconds = normalizeTugTime(form.timeSeconds);
  return timeSeconds != null ? Math.round(timeSeconds * 1000) : 0;
};

const AttemptSummaryCard = ({
  attempt,
  label,
  highlight = false,
}: {
  attempt: TugAttemptPayload;
  label: string;
  highlight?: boolean;
}) => {
  const timeSeconds = normalizeTugTime(attempt.timeSeconds);
  const severity = getTUGSeverityLabel(timeSeconds, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const severityTone = getTUGSeverityTone(timeSeconds, attempt.completed !== false, Boolean(attempt.physicalAssistance));
  const comparable = isTUGComparableToReference(attempt);

  return (
    <div className={`rounded-[24px] border p-5 shadow-sm ${highlight ? 'border-cyan-200 bg-cyan-50/70' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">
            {attempt.completed !== false && timeSeconds != null ? `${formatTugSeconds(timeSeconds)} s` : 'Não concluiu'}
          </div>
        </div>
        <div className={`rounded-full px-3 py-1 text-xs font-bold ${severityTone}`}>{severity}</div>
      </div>

      <div className="mt-4 grid gap-3 text-sm text-slate-600 md:grid-cols-2">
        <div>
          <span className="font-medium text-slate-800">Auxílio:</span> {attempt.walkingAidLabel}
        </div>
        <div>
          <span className="font-medium text-slate-800">Comparabilidade:</span> {comparable ? 'Comparável' : 'Cautela'}
        </div>
        <div>
          <span className="font-medium text-slate-800">Familiarização:</span> {attempt.familiarizationPerformed ? 'Registrada' : 'Não registrada'}
        </div>
        <div>
          <span className="font-medium text-slate-800">Assistência física:</span> {attempt.physicalAssistance ? 'Sim' : 'Não'}
        </div>
      </div>

      {attempt.observations ? <p className="mt-4 text-sm leading-relaxed text-slate-600">{attempt.observations}</p> : null}
    </div>
  );
};

export default function TUGTest({ initial = null, patientContext, onCancel, onDraftAttempt, onNext }: TUGTestProps) {
  const normalizedInitial = normalizeTugPayload(initial);
  const initialAttempt1 = normalizedInitial?.attempt1 ?? null;
  const initialAwaitingSecondAttempt = isTugAwaitingSecondAttempt(normalizedInitial);
  const registeredWeightKg = normalizePatientWeightKg(patientContext?.weightKg);
  const initialWeightKg = normalizePatientWeightKg(normalizedInitial?.patientContext?.weightKg);
  const initialFormState =
    initialAwaitingSecondAttempt && initialAttempt1
      ? buildFreshAttemptFormState(initialAttempt1)
      : buildAttemptFormState(initialAttempt1);

  const [attemptStage, setAttemptStage] = useState<'attempt1' | 'decision' | 'attempt2'>(
    initialAwaitingSecondAttempt && initialAttempt1 ? 'decision' : 'attempt1',
  );
  const [attemptNumber, setAttemptNumber] = useState<1 | 2>(initialAwaitingSecondAttempt && initialAttempt1 ? 2 : 1);
  const [attempt1Result, setAttempt1Result] = useState<TugAttemptPayload | null>(initialAttempt1);
  const [weightKg, setWeightKg] = useState<number | null>(initialWeightKg);
  const [form, setForm] = useState<TugAttemptFormState>(initialFormState);
  const [savingAttempt, setSavingAttempt] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const initialElapsedMs = resolveElapsedMsFromForm(initialFormState);
  const [timerRunning, setTimerRunning] = useState<boolean>(false);
  const [elapsedMs, setElapsedMs] = useState<number>(initialElapsedMs);
  const timerStartedAtRef = useRef<number | null>(null);
  const timerAccumulatedRef = useRef<number>(initialElapsedMs);
  const timerIntervalRef = useRef<number | null>(null);

  const normalizedTime = useMemo(() => normalizeTugTime(form.timeSeconds), [form.timeSeconds]);
  const walkingAidLabel = useMemo(
    () => getTUGWalkingAidLabel(form.walkingAid, form.customAidDescription),
    [form.customAidDescription, form.walkingAid],
  );
  const stopwatchLabel = useMemo(() => formatStopwatch(elapsedMs), [elapsedMs]);
  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      ageYears: toFiniteNumber(patientContext?.ageYears),
      weightKg,
      heightCm: normalizePatientHeightCm(patientContext?.heightCm),
    }),
    [patientContext, weightKg],
  );
  const previewReferenceComparison = useMemo(
    () => calculateTugReferenceComparison(normalizedTime, effectivePatientContext),
    [effectivePatientContext, normalizedTime],
  );
  const previewLabel = getTUGSeverityLabel(normalizedTime, form.completed, form.physicalAssistance, previewReferenceComparison);
  const previewRange = getTUGSeverityRangeLabel(normalizedTime, form.completed, form.physicalAssistance, previewReferenceComparison);
  const previewInterpretation = getTUGInterpretationText({
    completed: form.completed,
    timeSeconds: normalizedTime,
    walkingAidLabel,
    physicalAssistance: form.physicalAssistance,
    familiarizationPerformed: form.familiarizationPerformed,
    reasonNotCompleted: form.reasonNotCompleted,
    referenceComparison: previewReferenceComparison,
  });

  const formValid =
    weightKg != null &&
    ((form.completed && normalizedTime != null && (form.walkingAid !== 'other' || form.customAidDescription.trim().length > 0)) ||
      (!form.completed &&
        form.reasonNotCompleted.trim().length > 0 &&
        (form.walkingAid !== 'other' || form.customAidDescription.trim().length > 0)));

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const startedAt = timerStartedAtRef.current;
      if (startedAt == null) return;

      setElapsedMs(timerAccumulatedRef.current + (Date.now() - startedAt));
    }, 100);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerRunning]);

  useEffect(() => {
    if (form.completed) return;
    setTimerRunning(false);
  }, [form.completed]);

  const resetTimerState = (nextElapsedMs = 0) => {
    timerStartedAtRef.current = null;
    timerAccumulatedRef.current = nextElapsedMs;
    setElapsedMs(nextElapsedMs);
    setTimerRunning(false);
  };

  const applyFormState = (nextForm: TugAttemptFormState) => {
    setForm(nextForm);
    resetTimerState(resolveElapsedMsFromForm(nextForm));
  };

  const syncTimerToField = (nextElapsedMs: number) => {
    if (nextElapsedMs <= 0) return;
    setForm((current) => ({ ...current, timeSeconds: formatElapsedSecondsInput(nextElapsedMs) }));
  };

  const buildAttemptPayload = (): TugAttemptPayload | null => {
    if (weightKg == null) {
      setInlineError('Informe ou confirme o peso usado nesta avaliação antes de salvar a tentativa.');
      return null;
    }

    if (!formValid) {
      setInlineError('Preencha os campos obrigatórios antes de salvar esta tentativa.');
      return null;
    }

    return {
      completed: form.completed,
      timeSeconds: form.completed ? normalizedTime : null,
      walkingAid: form.walkingAid,
      walkingAidLabel,
      customAidDescription: form.walkingAid === 'other' ? form.customAidDescription.trim() : undefined,
      familiarizationPerformed: form.familiarizationPerformed,
      physicalAssistance: form.physicalAssistance,
      reasonNotCompleted: !form.completed ? form.reasonNotCompleted.trim() : undefined,
      observations: form.observations.trim() || undefined,
      finishedAt: new Date().toISOString(),
    };
  };

  const handleStartTimer = () => {
    if (!form.completed || timerRunning) return;

    timerStartedAtRef.current = Date.now();
    setTimerRunning(true);
  };

  const handlePauseTimer = () => {
    if (!timerRunning) return;

    const startedAt = timerStartedAtRef.current;
    const nextElapsedMs = startedAt == null ? timerAccumulatedRef.current : timerAccumulatedRef.current + (Date.now() - startedAt);
    timerAccumulatedRef.current = nextElapsedMs;
    timerStartedAtRef.current = null;
    setElapsedMs(nextElapsedMs);
    setTimerRunning(false);
    syncTimerToField(nextElapsedMs);
  };

  const handleResetTimer = () => {
    resetTimerState(0);
  };

  const handleUseTimerValue = () => {
    syncTimerToField(elapsedMs);
  };

  const handleSaveAttempt = async () => {
    if (savingAttempt) return;

    setInlineError(null);
    const attemptPayload = buildAttemptPayload();
    if (!attemptPayload) return;

    setSavingAttempt(true);
    try {
      if (attemptNumber === 1) {
        const draftPayload = buildTugPayload(attemptPayload, {
          workflowStatus: 'awaiting_second_attempt',
          protocol: TUG_STANDARD_PROTOCOL,
          patientContext: effectivePatientContext,
        });

        if (onDraftAttempt) {
          const persisted = await onDraftAttempt(draftPayload);
          if (persisted === false) return;
        }

        setAttempt1Result(attemptPayload);
        applyFormState(buildFreshAttemptFormState(attemptPayload));
        setAttemptStage('decision');
        setAttemptNumber(2);
        return;
      }

      if (!attempt1Result) {
        setInlineError('A primeira tentativa do TUG precisa ser concluída antes da segunda.');
        return;
      }

      onNext(
        buildTugPayload(attempt1Result, {
          attempt2: attemptPayload,
          workflowStatus: 'completed',
          protocol: TUG_STANDARD_PROTOCOL,
          patientContext: effectivePatientContext,
        }),
      );
    } finally {
      setSavingAttempt(false);
    }
  };

  const handleCompleteWithOneAttempt = () => {
    if (!attempt1Result) {
      setInlineError('A primeira tentativa ainda não foi concluída.');
      return;
    }

    onNext(
      buildTugPayload(attempt1Result, {
        workflowStatus: 'completed',
        protocol: TUG_STANDARD_PROTOCOL,
        patientContext: effectivePatientContext,
      }),
    );
  };

  const handleStartSecondAttempt = () => {
    if (!attempt1Result) {
      setInlineError('A primeira tentativa ainda não foi concluída.');
      return;
    }

    setInlineError(null);
    applyFormState(buildFreshAttemptFormState(attempt1Result));
    setAttemptStage('attempt2');
    setAttemptNumber(2);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-blue-950">
        <div className="flex items-start gap-3">
          <TimerReset className="mt-0.5 h-5 w-5 text-blue-700" />
          <div className="space-y-2">
            <div>
              <p className="font-semibold">Timed Up and Go (TUG)</p>
              <p className="text-sm text-blue-900/80">
                Registrar o tempo para levantar de uma cadeira com braços, caminhar 3 metros, girar, retornar e sentar.
              </p>
            </div>
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <div>Cadeira padrão com aproximadamente 46 cm de altura.</div>
              <div>Usar calçado habitual e auxílio de marcha habitual, se necessário.</div>
              <div>Realizar uma tentativa prévia de familiarização antes da medida cronometrada.</div>
              <div>Usar o melhor tempo entre tentativas e interpretar com os limiares de 11,2 s, 12 s e 13,8 s.</div>
            </div>
          </div>
        </div>
      </div>

      <AssessmentWeightAdjustmentField
        value={weightKg}
        baselineValue={registeredWeightKg}
        onChange={setWeightKg}
        helperText="Informe o peso aferido nesta avaliação ou confirme o valor do cadastro. O valor fica registrado somente neste TUG."
      />

      {attemptStage === 'decision' && attempt1Result ? (
        <div className="space-y-4">
          <AttemptSummaryCard attempt={attempt1Result} label="1ª tentativa salva" highlight />

          <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700">Fluxo em 2 tentativas</div>
            <h4 className="mt-2 text-xl font-black tracking-tight">A 1ª tentativa já foi salva</h4>
            <p className="mt-2 text-sm leading-relaxed text-emerald-900/80">
              Você pode encerrar o TUG com uma única tentativa ou iniciar a 2ª tentativa para considerar o melhor tempo, como feito no TC6.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCompleteWithOneAttempt}
                className="inline-flex items-center rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                <Check className="mr-2 h-4 w-4" />
                Encerrar com 1 tentativa
              </button>
              <button
                type="button"
                onClick={handleStartSecondAttempt}
                className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
              >
                <Play className="mr-2 h-4 w-4" />
                Iniciar 2ª tentativa
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {attemptNumber === 2 && attempt1Result ? <AttemptSummaryCard attempt={attempt1Result} label="1ª tentativa" /> : null}

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
                  {attemptNumber === 1 ? 'Primeira tentativa' : 'Segunda tentativa'}
                </div>
                <h4 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                  {attemptNumber === 1 ? '1ª tentativa do TUG' : '2ª tentativa do TUG'}
                </h4>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                {attemptNumber === 1 ? 'Salvar e decidir a 2ª' : 'Melhor tempo entre 2 tentativas'}
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Status do teste</label>
              <div className="grid gap-2 sm:grid-cols-2">
                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${
                    form.completed ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <input
                    type="radio"
                    name={`tug_status_${attemptNumber}`}
                    checked={form.completed}
                    onChange={() => setForm((current) => ({ ...current, completed: true }))}
                  />
                  <span className="font-medium">Concluído</span>
                </label>
                <label
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${
                    !form.completed ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  <input
                    type="radio"
                    name={`tug_status_${attemptNumber}`}
                    checked={!form.completed}
                    onChange={() => setForm((current) => ({ ...current, completed: false }))}
                  />
                  <span className="font-medium">Não concluiu</span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Auxílio de marcha utilizado</label>
              <select
                value={form.walkingAid}
                onChange={(event) => setForm((current) => ({ ...current, walkingAid: event.target.value as TugWalkingAid }))}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              >
                {TUG_WALKING_AID_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {form.walkingAid === 'other' && (
                <input
                  value={form.customAidDescription}
                  onChange={(event) => setForm((current) => ({ ...current, customAidDescription: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                  placeholder="Descrever o dispositivo utilizado"
                />
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Tempo do teste (segundos)</label>
              <input
                value={form.timeSeconds}
                onChange={(event) => setForm((current) => ({ ...current, timeSeconds: event.target.value }))}
                disabled={!form.completed}
                inputMode="decimal"
                placeholder="Ex.: 12,4"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-slate-500">Registrar o tempo da tentativa cronometrada após a familiarização.</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Motivo se não concluiu</label>
              <input
                value={form.reasonNotCompleted}
                onChange={(event) => setForm((current) => ({ ...current, reasonNotCompleted: event.target.value }))}
                disabled={form.completed}
                placeholder="Ex.: não conseguiu levantar sem ajuda"
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="rounded-[24px] border border-cyan-100 bg-cyan-50/70 p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-700">Cronômetro de apoio</div>
                <div className="mt-2 text-4xl font-black tracking-tight text-cyan-950">{stopwatchLabel}</div>
                <p className="mt-2 text-sm text-cyan-900/80">Use como apoio no runner e transfira o tempo para o campo ao final da medida.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {timerRunning ? (
                  <button
                    type="button"
                    onClick={handlePauseTimer}
                    className="inline-flex items-center rounded-xl bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600"
                  >
                    <Pause className="mr-2 h-4 w-4" />
                    Pausar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleStartTimer}
                    disabled={!form.completed}
                    className="inline-flex items-center rounded-xl bg-cyan-600 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Play className="mr-2 h-4 w-4" />
                    {elapsedMs > 0 ? 'Retomar' : 'Iniciar'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleUseTimerValue}
                  disabled={!form.completed || elapsedMs <= 0}
                  className="inline-flex items-center rounded-xl border border-cyan-200 bg-white px-3 py-2 text-sm font-medium text-cyan-800 hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Check className="mr-2 h-4 w-4" />
                  Usar no campo
                </button>
                <button
                  type="button"
                  onClick={handleResetTimer}
                  disabled={elapsedMs <= 0 && !timerRunning}
                  className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Zerar
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <input
                type="checkbox"
                checked={form.familiarizationPerformed}
                onChange={(event) => setForm((current) => ({ ...current, familiarizationPerformed: event.target.checked }))}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900">Tentativa de familiarização realizada</div>
                <div className="text-sm text-slate-600">Mantém aderência ao protocolo descrito no estudo original.</div>
              </div>
            </label>

            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <input
                type="checkbox"
                checked={form.physicalAssistance}
                onChange={(event) => setForm((current) => ({ ...current, physicalAssistance: event.target.checked }))}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-slate-900">Necessitou assistência física</div>
                <div className="text-sm text-slate-600">Se marcado, o resultado fica menos comparável aos estratos originais do TUG.</div>
              </div>
            </label>
          </div>

          {form.physicalAssistance && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4" />
              <p>O protocolo original do TUG não prevê assistência física. O tempo pode ser salvo, mas a interpretação ficará sinalizada como cautelosa.</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Observações clínicas</label>
            <textarea
              rows={3}
              value={form.observations}
              onChange={(event) => setForm((current) => ({ ...current, observations: event.target.value }))}
              className="w-full rounded-2xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              placeholder="Ex.: giro inseguro, hesitação ao sentar, uso de impulso nos braços..."
            />
          </div>

          <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Prévia automática</div>
                <h4 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                  {form.completed && normalizedTime != null ? `${formatTugSeconds(normalizedTime)} s` : 'Sem tempo válido'}
                </h4>
              </div>
              <div className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-700 shadow-sm">
                {previewLabel} • {previewRange}
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">{previewInterpretation}</p>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">Referência prevista</div>
              <div className="mt-1">
                {getTUGReferenceComparisonNote(previewReferenceComparison, effectivePatientContext)}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
        <Info className="mt-0.5 h-4 w-4 text-slate-500" />
        <p>{TUG_REFERENCE_NOTE}</p>
      </div>

      {inlineError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{inlineError}</div>
      ) : null}

      <div className="flex items-center justify-between pt-1">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>

        {attemptStage === 'decision' ? (
          <div className="text-xs text-slate-500">A primeira tentativa ficou salva como rascunho.</div>
        ) : (
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50"
            disabled={!formValid || savingAttempt}
            onClick={() => void handleSaveAttempt()}
          >
            {attemptNumber === 1 ? 'Salvar 1ª tentativa' : 'Concluir TUG'}
            <Check className="ml-1 h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
